import { IonInfiniteScroll } from '@ionic/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useMemo } from 'react'

import {
  QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS_INFINITE,
  useManagementSearchUnitTaskGroupsCount,
  useManagementSearchUnitTaskGroupsInfinite
} from '@apps-common/apis/management/unit-task/task-groups'
import { UnitListCardSmSkeleton } from '@apps-common/apps/portal/pages/units/components'
import { TaskHeader } from '@apps-common/apps/portal/pages/units/pages/program-tasks/components'
import { TaskCard } from '@apps-common/apps/portal/pages/units/pages/program-tasks/components/tasks-infinite'
import { useTaskGroupUpdates } from '@apps-common/apps/portal/pages/units/pages/program-tasks/hooks'
import { InfiniteLoader, PullRefresh } from '@apps-common/components/common'
import { LayoutContainer, LayoutPadding } from '@apps-common/components/layout'
import { EndOfResults } from '@apps-common/components/modals/unit-history'
import { PageClearFilterButton, PageResultsCount } from '@apps-common/components/page'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { queryClient } from '@apps-common/lib'
import { useUnitTaskGroupsFilterStore } from '@apps-common/stores/useUnitTaskGroupsFilterStore'
import { IUnitTaskGroupModel } from '@apps-common/types/portal/unit-tasks'

const containerId = 'tasks-group-infinite-container'

export const TasksInfiniteView = () => {
  const filter = useUnitTaskGroupsFilterStore()
  const navigation = useNavigation()

  const {
    isLoading: countIsLoading,
    isError: countIsError,
    data: countData
  } = useManagementSearchUnitTaskGroupsCount({
    model: filter
  })

  const {
    isLoading: tasksLoading,
    isFetching: tasksFetching,
    isRefetching: tasksRefetching,
    isError: tasksError,
    data: tasks,
    refetch: tasksRefetch,
    fetchNextPage: tasksFetchNext,
    hasNextPage: tasksHasNextPage
  } = useManagementSearchUnitTaskGroupsInfinite({
    enabled: true,
    model: filter
  })

  const data = useMemo(
    () => tasks?.pages?.reduce((acc: IUnitTaskGroupModel[], curr: IUnitTaskGroupModel[]) => [...acc, ...curr], []) || [],
    [tasks]
  )

  const allLoading = tasksLoading || tasksFetching || tasksError || countIsLoading || countIsError

  useTaskGroupUpdates(containerId, tasksLoading, allLoading, data)

  const countLoading = countIsLoading || countIsError
  const loading = tasksLoading || tasksError

  return (
    <>
      <TaskHeader>
        <div className='flex items-center justify-between'>
          <PageResultsCount loading={countLoading} count={countData} extraText={filter.status?.replaceAll('_', ' ')} />
          <PageClearFilterButton filter={filter} filterKeyWordsFunc={filter.getFilteredByKeywords} callback={filter.clear} />
        </div>
      </TaskHeader>
      <LayoutContainer id={containerId}>
        <LayoutPadding topPadding='pt-[40px] sm:pt-[70px]'>
          <PullRefresh
            loading={tasksRefetching}
            callback={() => {
              queryClient.removeQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS_INFINITE(filter) })
              tasksRefetch()
            }}
          />
          <>
            <div className='grid sm:grid-cols-2 sm:max-w-[1200px] gap-[15px]'>
              <AnimatePresence>
                {loading &&
                  Array.from(Array(25).keys()).map((k, i) => (
                    <motion.div key={`motion-program-task-list-card-skeleton-${i}`} {...motionFadeIn(0.2)}>
                      <UnitListCardSmSkeleton
                        index={i}
                        bgClasses='bg-surface border border-border min-w-full sm:min-w-[200px] lg:min-w-[250px]'
                        textClasses='text-text'
                        iconClasses='text-text'
                        bgSecondaryClasses='bg-skeleton'
                        textSecondaryClasses='text-text'
                        bgTertiaryClasses='bg-skeleton-hi'
                      />
                    </motion.div>
                  ))}
                {!loading &&
                  tasks &&
                  data.map((t: IUnitTaskGroupModel) => (
                    <motion.div key={`motion-program-task-list-card-${t?.id}`} {...motionFadeIn(0.2)}>
                      <TaskCard task={t} navigation={navigation} />
                    </motion.div>
                  ))}
                <EndOfResults show={!tasksHasNextPage} />
              </AnimatePresence>
            </div>
            <IonInfiniteScroll
              onIonInfinite={async (ev) => {
                await tasksFetchNext()
                ev.target.complete()
              }}
            >
              <InfiniteLoader key='program-tasks-infinite-loader' loading={tasksFetching} />
            </IonInfiniteScroll>
          </>
        </LayoutPadding>
      </LayoutContainer>
    </>
  )
}
